import React, { useState } from "react";
import { useDispatch } from "react-redux";
import format from "date-fns/format";
import clsx from "clsx";

import { Button, DialogActions } from "@material-ui/core";

import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import Textarea from "components/Textarea";
import { useClickBlockerContext } from "components/BlockableClickContext";
import { setStoreActiveLinkCandidate } from "store/actions/sidebar";
import { EMPTY_STRING } from "common/constants";
import { TExamNote } from "../../types";

import useStyles from "../styles";

interface IProps {
  open: boolean;
  isLoading: boolean;
  examNote: TExamNote;
  additionalDesktopClassNames: string;
  note: string;
  onClose: () => void;
  onSave: () => Promise<void>;
  onDelete: (event: React.MouseEvent) => void;
  onNoteChange: (
    event: React.ChangeEvent<{
      value: string;
    }>
  ) => void;
  isNoteExists: boolean;
  index: number;
  noteLabel: string;
}

const ContentDesktop = (props: IProps) => {
  const {
    open,
    onDelete,
    onClose,
    onSave,
    onNoteChange,
    note,
    isNoteExists,
    index,
    examNote,
    additionalDesktopClassNames,
    noteLabel
  } = props;

  const UNSAVED_CHANGES_TEXT = `You have unsaved changes to your ${noteLabel.toLowerCase()}. Would you like to keep or discard these changes?`;

  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    blockClick,
    setBlockClick,
    setBlockClickCallback,
    setBlockActions,
    pendingActionRef
  } = useClickBlockerContext();
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);

  const callbackLocked = () => setUnsavedChangesDialogOpen(true);

  const lockNavigation = callback => {
    pendingActionRef.current = null;
    setBlockClick(true);
    setBlockClickCallback(() => callback);
    setBlockActions(true);
  };

  const unlockNavigation = () => {
    setBlockClick(false);
    setBlockClickCallback(null);
    setBlockActions(false);
  };

  const runPendingAction = () => {
    const pendingAction = pendingActionRef.current;

    pendingActionRef.current = null;
    pendingAction?.();
  };

  const handleUnsavedChangesSave = async () => {
    unlockNavigation();
    setUnsavedChangesDialogOpen(false);
    await onSave();
    runPendingAction();
  };

  const handleChangeNote = e => {
    const isChanged = e.trim() !== (examNote?.body?.trim() ?? EMPTY_STRING);

    if (isChanged && !blockClick) {
      lockNavigation(callbackLocked);
    } else if (!isChanged && blockClick) {
      unlockNavigation();
    }

    onNoteChange(e);
  };

  const handleClose = () => {
    unlockNavigation();
    setUnsavedChangesDialogOpen(false);
    onClose();
  };

  const handleUnsavedChangesDiscard = () => {
    handleClose();
    runPendingAction();
  };

  const handleSave = () => {
    unlockNavigation();
    onSave();
  };

  const handleDelete = e => {
    unlockNavigation();
    onDelete(e);
  };

  const handleCancelClick = () => {
    setUnsavedChangesDialogOpen(false);
    pendingActionRef.current = null;
    dispatch(setStoreActiveLinkCandidate(EMPTY_STRING));
  };

  const unsavedChangesDialogContentRender = () => <>{UNSAVED_CHANGES_TEXT}</>;

  return (
    <>
      {open && (
        <div className={classes.dialogPaper}>
          <div className={classes.desktopHeader}>
            {isNoteExists ? `Edit ${noteLabel}` : `Add ${noteLabel}`}
          </div>
          <Textarea
            className={clsx(
              classes.textarea,
              { [classes.rowOdd]: index % 2 },
              additionalDesktopClassNames
            )}
            maxLength={90}
            label={format(new Date(), "MM/dd-yyyy - hh:mm aa")}
            placeholder={"Add Note"}
            rows={4}
            value={note}
            defaultValue={examNote?.body}
            onChange={handleChangeNote}
            classNameCounter={clsx(
              classes.textarea,
              { [classes.rowOdd]: index % 2 },
              additionalDesktopClassNames
            )}
          />
          <DialogActions className={classes.dialogActions}>
            {examNote?.id && (
              <Button className={classes.buttonDelete} onClick={handleDelete}>
                DELETE
              </Button>
            )}
            <div>
              <Button className={classes.buttonCancel} onClick={handleClose}>
                CANCEL
              </Button>
              <Button
                className={classes.buttonSave}
                disabled={!note.trim().length}
                onClick={handleSave}
              >
                SAVE
              </Button>
            </div>
          </DialogActions>
        </div>
      )}
      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onDiscard={handleUnsavedChangesDiscard}
        onSave={handleUnsavedChangesSave}
        onCancel={handleCancelClick}
        content={unsavedChangesDialogContentRender()}
      />
    </>
  );
};

export default ContentDesktop;
